"use client";

import { useSyncExternalStore } from "react";

// docs/specs/foundation/shared-ui-kit.md rules 22–25 (FND-16, U2): unsaved create/edit form state
// lives in this tab's sessionStorage, keyed by user + document type + document id (or "new"), so
// a reload, a failed save or a sign-in after the session expired loses nothing. It's cleared on a
// successful save, on discard and on sign-out, and never holds files, passwords or tokens.

const PREFIX = "draft:";

export const draftKey = (userId: string, documentType: string, documentId: string | null) =>
  `${PREFIX}${userId}:${documentType}:${documentId ?? "new"}`;

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** The stored JSON, or null. Failures (blocked storage) read as nothing stored. */
export function readDraft(key: string): string | null {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function writeDraft(key: string, values: unknown): void {
  try {
    storage()?.setItem(key, JSON.stringify(values));
  } catch {
    // Full or blocked: the form still has the values in memory
  }
}

export function clearDraft(key: string): void {
  try {
    storage()?.removeItem(key);
  } catch {
    // Nothing to clear
  }
}

/** Rule 24: sign-out leaves no draft behind in this tab. */
export function clearAllDrafts(): void {
  const store = storage();
  if (!store) return;
  try {
    const keys = Array.from({ length: store.length }, (_, i) => store.key(i));
    for (const key of keys) if (key?.startsWith(PREFIX)) store.removeItem(key);
  } catch {
    // Nothing to clear
  }
}

const firstReads = new Map<string, string | null>();
const noSubscription = () => () => {};

/**
 * The draft that was stored under `key` when the page loaded (null on the server and while
 * hydrating), so the form can start from it. Later writes don't change it: the form owns its
 * values from then on. Render the form with `key={restored ? "restored" : "fresh"}`.
 */
export function useRestoredDraft(key: string): string | null {
  return useSyncExternalStore(
    noSubscription,
    () => {
      if (!firstReads.has(key)) firstReads.set(key, readDraft(key));
      return firstReads.get(key) ?? null;
    },
    () => null,
  );
}
